/**
 * Browser-side file attachment helper for the objective chat input.
 *
 * Replaces the old pdfjs-based extraction pipeline. Gemini 3.x ingests PDFs,
 * images, and short text files natively as `inlineData` parts, so the only
 * client-side work we need is: validate type, validate size, and base64-encode
 * the raw bytes for transport. The worker then forwards the bytes to Gemini
 * verbatim.
 *
 * @module lib/file-attachment
 */

/**
 * Hard cap on inline attachment size (bytes). Gemini's documented inline
 * ceiling is 50MB for PDFs / 7MB for text-plain; we pick a single 15MB limit
 * to leave headroom for base64 expansion (~33%) and keep the JSON request
 * body comfortably under Cloudflare's worker request limit.
 */
export const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;

/**
 * MIME types Gemini's vision pipeline understands meaningfully. Sticking to
 * this set keeps the UX honest: the model will actually look at the file's
 * contents (layout, images, text), not just extract bytes as a string.
 *
 * Sources: Gemini docs at ai.google.dev/gemini-api/docs/document-processing
 * (PDF supported up to 50MB / 1000 pages) and the model's image MIME list
 * (image/png, image/jpeg, image/webp, image/heic, image/heif).
 */
export const SUPPORTED_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/heic',
  'image/heif',
  'text/plain',
  'text/markdown',
] as const;

/** File-picker `accept` attribute matching {@link SUPPORTED_MIME_TYPES}. */
export const ACCEPTED_FILE_TYPES =
  '.pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.txt,.md,.markdown,application/pdf,image/png,image/jpeg,image/webp,image/heic,image/heif,text/plain,text/markdown';

/**
 * Wire shape of an inline attachment, mirroring Gemini's REST `inline_data`
 * part. The worker forwards this verbatim into the `parts` array on a
 * `generateContent` call.
 */
export interface InlineAttachment {
  /** MIME type as Gemini expects it (e.g. `application/pdf`, `image/png`). */
  mimeType: string;
  /** Base64-encoded raw bytes of the file. */
  dataBase64: string;
  /** Original filename, retained so the user sees what's attached. */
  fileName: string;
  /** Decoded byte length, for UI display + diagnostics. */
  byteLength: number;
}

/**
 * Resolve a file's MIME type, falling back to extension when the browser
 * left `file.type` empty (common for `.md`/`.markdown` and HEIC on some
 * platforms). Returns the empty string when the type is still unknown.
 */
function resolveMimeType(file: File): string {
  if (file.type) return file.type.toLowerCase();
  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf')) return 'application/pdf';
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  if (name.endsWith('.webp')) return 'image/webp';
  if (name.endsWith('.heic')) return 'image/heic';
  if (name.endsWith('.heif')) return 'image/heif';
  if (name.endsWith('.md') || name.endsWith('.markdown')) return 'text/markdown';
  if (name.endsWith('.txt')) return 'text/plain';
  return '';
}

/**
 * Encode an `ArrayBuffer` to a base64 string without blowing the call stack.
 * `btoa(String.fromCharCode(...bytes))` works for small buffers but throws
 * on multi-MB payloads (RangeError: too many arguments). We chunk in 32KB
 * slices to stay safe across all browsers including Safari.
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + CHUNK)),
    );
  }
  return btoa(binary);
}

/**
 * Convert a browser `File` into an `InlineAttachment`. Validates the MIME
 * type + size up front; throws on rejection so the chat input can surface
 * the message inline.
 *
 * @throws `Error` with a user-friendly message when the type is unsupported
 *         or the size exceeds {@link MAX_ATTACHMENT_BYTES}.
 */
export async function buildInlineAttachment(file: File): Promise<InlineAttachment> {
  const mimeType = resolveMimeType(file);
  if (!mimeType) {
    throw new Error(
      `Unsupported file: ${file.name}. Drop a PDF, image, or text file.`,
    );
  }
  if (!SUPPORTED_MIME_TYPES.includes(mimeType as (typeof SUPPORTED_MIME_TYPES)[number])) {
    throw new Error(
      `${mimeType} isn't supported yet. Drop a PDF, image, or text file.`,
    );
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    const limitMb = Math.round(MAX_ATTACHMENT_BYTES / (1024 * 1024));
    const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
    throw new Error(
      `File is too large (${sizeMb}MB). The cap is ${limitMb}MB.`,
    );
  }

  const buffer = await file.arrayBuffer();
  return {
    mimeType,
    dataBase64: arrayBufferToBase64(buffer),
    fileName: file.name,
    byteLength: file.size,
  };
}
